/**
 * CSV Download Modal
 *
 * Modal for selecting date range before downloading consent CSV.
 * Matches Angular implementation with 31-day validation and 10000 row warning.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerOverlay,
  DatePicker,
  Button,
} from 'equal-ds-ui';
import { Download, AlertCircle, Calendar } from 'lucide-react';

interface CsvDownloadModalProps {
  onClose: () => void;
  onDownload: (startDate: Date, endDate: Date) => void;
  isDownloading?: boolean;
  isJobBasedDownload?: boolean;
}

/**
 * Validate date range with UTC-based calculation to avoid DST issues.
 * Returns validation result with optional error message.
 */
const validateDateRange = (
  startDate: Date | null,
  endDate: Date | null
): { valid: boolean; error?: string } => {
  if (!startDate || !endDate) {
    return { valid: false };
  }

  // Check end date is not before start date
  if (endDate.getTime() < startDate.getTime()) {
    return { valid: false, error: 'End date cannot be before start date' };
  }

  // Use UTC-based calculation to avoid DST boundary issues
  const utcStart = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const utcEnd = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const daysDifference = Math.floor((utcEnd - utcStart) / 86400000);

  if (daysDifference > 31) {
    return { valid: false, error: 'Please select a range within 31 days' };
  }

  return { valid: true };
};

export const CsvDownloadModal: React.FC<CsvDownloadModalProps> = ({
  onClose,
  onDownload,
  isDownloading = false,
  isJobBasedDownload = false,
}) => {
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const [dateError, setDateError] = useState<string | null>(null);

  // Memoized validation result - only recomputes when dateRange changes
  const isDateRangeValid = useMemo(() => {
    const result = validateDateRange(dateRange.startDate, dateRange.endDate);
    return result.valid;
  }, [dateRange.startDate, dateRange.endDate]);

  // Handle date range change with validation
  const handleDateRangeChange = useCallback((range: { startDate: Date | null; endDate: Date | null; startTime: unknown; endTime: unknown }) => {
    const newStartDate = range.startDate || null;
    const newEndDate = range.endDate || null;

    setDateRange({
      startDate: newStartDate,
      endDate: newEndDate,
    });

    // Validate and show errors in the modal (not inside the date picker)
    if (newStartDate && newEndDate) {
      const result = validateDateRange(newStartDate, newEndDate);
      if (!result.valid && result.error) {
        setDateError(result.error);
        return;
      }
    }

    // Clear error if validation passes or dates are incomplete
    setDateError(null);
  }, []);

  const handleDownload = useCallback((e: React.MouseEvent) => {
    // Prevent any event bubbling or default behavior
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple clicks - parent's isDownloading is sufficient
    if (isDownloading) {
      return;
    }

    const { startDate, endDate } = dateRange;

    // Validate dates are selected
    if (!startDate || !endDate) {
      setDateError('Please select both start and end dates');
      return;
    }

    // Validate using the centralized function
    const result = validateDateRange(startDate, endDate);
    if (!result.valid) {
      setDateError(result.error || 'Invalid date range');
      return;
    }

    // Clear error and trigger download
    setDateError(null);
    onDownload(startDate, endDate);
  }, [dateRange, isDownloading, onDownload]);

  const handleCancel = useCallback(() => {
    setDateError(null);
    setDateRange({ startDate: null, endDate: null });
    onClose();
  }, [onClose]);

  return (
    <>
      <style>
        {`
          /* Ensure DatePicker calendar dropdown appears above modal drawer */
          .csv-modal-datepicker-dropdown {
            z-index: 10001 !important;
          }
        `}
      </style>
      <DrawerOverlay style={{ zIndex: 9999 }} />
      <Drawer
        variant="overlay"
        width="500px"
        className="border-border-default bg-white"
        style={{ zIndex: 10000 }}
      >
        <DrawerHeader
          title="Select Date Range"
          subtitle="Choose date range to download consent request data"
          icon={<Calendar className="w-5 h-5" />}
          onClose={handleCancel}
        />

        <DrawerContent className="flex flex-col p-6 space-y-6">
          {/* Error Message */}
          {dateError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{dateError}</p>
            </div>
          )}

          {/* Date Range Picker */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Enter a date range
            </label>
            <DatePicker
              selectedRange={{
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                startTime: null,
                endTime: null,
              }}
              rangeMode={true}
              dateOnly={true}
              closeOnSelect={true}
              useSmartDefaults={false}
              maxDate={new Date()}
              onRangeChange={handleDateRangeChange}
              placeholder="Select date range"
              dateFormat="medium"
              variant="default"
              showCalendarIcon={false}
              triggerClassName="w-full"
              contentClassName="csv-modal-datepicker-dropdown"
            />
          </div>

          {/* Info Message */}
          {!isJobBasedDownload && (
            <div className="flex items-start gap-2 p-3 bg-slate-100 border border-slate-300 rounded-lg">
              <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600">
                Maximum 10000 rows data will be able to download in CSV file.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              variant="secondary"
              size="md"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCancel();
              }}
              disabled={isDownloading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleDownload}
              disabled={isDownloading || !isDateRangeValid}
            >
              {isDownloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </>
              )}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
